import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Button } from '../../../../components/common/buttons/Button';
import { formatKoreanNumber } from '../../../../utils/formatter/formatKoreanNumber.util';
import { getShortRegionAddress } from '../../../../utils/getter/getShortAddress.util';

interface FieldInfoSlideModalProps {
  isShow: boolean;
  landInfo: {
    address: string;
    landArea: number;
    areaInPyeong: number;
    pnu?: string;
  } | null;
  onRegister: () => void;
  onClose: () => void;
}

export const FieldInfoSlideModal = ({ isShow, landInfo, onRegister, onClose }: FieldInfoSlideModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalPositionY, setModalPositionY] = useState<string>('100%');

  // 평수 정수 변환 (표시용)
  const displayPyeong = useMemo(() => {
    return formatKoreanNumber(Math.round(landInfo?.areaInPyeong || 0));
  }, [landInfo?.areaInPyeong]);

  // 모달 열기 애니메이션
  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
    setTimeout(() => {
      setModalPositionY('0%');
    }, 100);
  }, []);

  // 모달 닫기 애니메이션
  const handleModalClose = useCallback(() => {
    setModalPositionY('100%');
    setTimeout(() => {
      setIsModalOpen(false);
    }, 150);
  }, []);

  useEffect(() => {
    if (isShow !== isModalOpen) {
      if (isShow) {
        handleModalOpen();
      } else {
        handleModalClose();
      }
    }
  }, [isShow, isModalOpen, handleModalOpen, handleModalClose]);

  if (!isModalOpen) {
    return null;
  }

  return (
    <ModalContainer modalPositionY={modalPositionY}>
      <ModalContent>
        <LandInfoContainer>
          <LandInfoTitle>{`${getShortRegionAddress(landInfo?.address || '')}`}</LandInfoTitle>
          <LandInfoContent>
            <LandInfoRow>
              <LandInfoLabel>{'평수'}</LandInfoLabel>
              <LandInfoValue>
                {landInfo?.landArea ? `${displayPyeong}평 (${landInfo.landArea.toLocaleString()}㎡)` : '-'}
              </LandInfoValue>
            </LandInfoRow>
          </LandInfoContent>
        </LandInfoContainer>

        <ButtonContainer>
          <Button
            size={'md'}
            borderType={'square'}
            color={'filledPrimaryStrong'}
            text={{ size: 'md', content: '작업 필지 등록하기' }}
            onClick={onRegister}
          />
        </ButtonContainer>
      </ModalContent>
    </ModalContainer>
  );
};

const ModalContainer = styled.div<{
  modalPositionY: string;
}>`
  position: fixed;
  bottom: 0px;
  left: 50%;

  width: 100%;
  max-width: 512px;

  transform: ${(props) => `translate(-50%, ${props.modalPositionY})`};
  -webkit-transform: ${(props) => `translate(-50%, ${props.modalPositionY})`};

  transition: all 150ms ease;
  -webkit-transition: all 150ms ease;

  z-index: 200;

  background: #fff;
  border-radius: 24px 24px 0px 0px;
  box-shadow: 0px -4px 20px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 24px;
  gap: 24px;
`;

const LandInfoContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LandInfoTitle = styled.div`
  white-space: pre-wrap;
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 27px */
`;

const LandInfoContent = styled.div`
  display: flex;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  border-radius: 8px;
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const LandInfoRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const LandInfoLabel = styled.div`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const LandInfoValue = styled.div`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: right;

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
  flex: 1 0 0;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  border-top: 1px dashed var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
`;
